import React, { useCallback, useRef} from "react";
import ChangePasswordModal, {CHANGE_PASSWORD_MODAL_ID} from "./ChangePasswordModal";
import authRepository from "../../repository/AuthRepository";
import useModal, {Modal} from "../../hook/useModal";
import {useQueryClient} from "@tanstack/react-query";
import {useIntl} from "react-intl";
import {errorToast, successToast} from "../../toast";
import ConfirmDialog from "../ConfirmDialog";
import exitIcon from "../../assets/svgs/exit.svg";
import {User} from "../../model/user";
import useLinkHandler from "../../hook/useLinkHandler";

type AccountSettingProps = {
    profile: User;
    containerClassName?: string;
};

const CREATE_CHANNEL_DIALOG_ID = "create-channel";

const AccountSetting: React.FC<AccountSettingProps> = (props) => {
    const { profile, containerClassName} = props;

    const intl = useIntl();
    const modal = useModal();

    const { linkToOnboarding } = useLinkHandler();

    const queryClient = useQueryClient();

    const nameRef = useRef<HTMLInputElement>(null);

    const onClickSaveName = async () => {
        const name = nameRef.current?.value;
        if (!name) {
            errorToast(intl.formatMessage({id: "i0068"}));
            return;
        }

        if (name === profile.fullName) {
            return;
        }

        if (name.length > 15) {
            errorToast(intl.formatMessage({id: "i0070"}));
            return;
        }

        try {
            await authRepository.updateName(name);
            successToast(intl.formatMessage({id: "i0069"}));

            await queryClient.refetchQueries({ queryKey: ["me"], exact: true })
        } catch (e) {
            errorToast(intl.formatMessage({id: "i0071"}));
        }
    }

    const onClickChangePassword = () => {
        modal.open(CHANGE_PASSWORD_MODAL_ID);
    };

    const onClickDeleteAccount = () => {
        modal.open(ACCOUNT_DELETE_CONFIRM_MODAL_ID);
    }

    return (
        <>
            <div className={containerClassName}>
                <div className="space-y-4">
                    <label className="console-input-label-sm">
                        {intl.formatMessage({id: "i0064"})}
                    </label>
                    <input type="email"
                           disabled={true}
                           className="console-form-input-2xl px-3"
                           placeholder={profile.email}/>
                </div>

                <div className="space-y-4">
                    <label htmlFor="name" className="console-input-label-sm">
                        {intl.formatMessage({id: "i0065"})}
                    </label>
                    <div className="flex justify-between space-x-1">
                        <input id="name" name="name"
                               className="console-form-input-2xl px-3"
                               placeholder={intl.formatMessage({id: "i0073"})}
                               defaultValue={profile.fullName}
                               ref={nameRef}
                        />
                        <button className="console-btn-2xl-primary w-[73px]"
                                onClick={onClickSaveName}
                        >
                            {intl.formatMessage({id: "i0066"})}
                        </button>
                    </div>
                </div>

                <hr/>

                <div className="flex justify-between">
                    <button className="console-btn-md2-gray"
                            onClick={onClickChangePassword}>
                        {intl.formatMessage({id: "i0067"})}
                    </button>

                    <button className="text-bunny-default text-[14px]"
                            onClick={onClickDeleteAccount}
                    >
                        {intl.formatMessage({id: "i0067_1"})}
                    </button>
                </div>

            </div>
            <ChangePasswordModal/>
            <AccountDeleteConfirmModal />

            <ConfirmDialog dialogId={CREATE_CHANNEL_DIALOG_ID}
                           content={intl.formatMessage({id: "i0072"})}
                           showCancelButton={true}
                           onConfirm={() => linkToOnboarding()}
                           confirmVariant={"primary"}
            />
        </>
    );
};

type AccountDeleteConfirmModalProps = {
}

const ACCOUNT_DELETE_CONFIRM_MODAL_ID = "channelId-deletion-confirm-modal-id";

const AccountDeleteConfirmModal: React.FC<AccountDeleteConfirmModalProps> = () => {
    const intl = useIntl();
    const modal = useModal();

    const queryClient = useQueryClient();

    const { linkToLogin } = useLinkHandler();

    const onClickCancel = () => {
        modal.close(ACCOUNT_DELETE_CONFIRM_MODAL_ID);
    }

    const onClickConfirm = useCallback(async () => {
        try {
            await authRepository.delete();
            queryClient.removeQueries({
                queryKey: ["me"]
            })
            linkToLogin();
            successToast(intl.formatMessage({id: "i0067_3"}))
        } catch (e) {
            errorToast(intl.formatMessage({id: "i0067_4"}))
        }
    }, [queryClient, linkToLogin, intl])

    return (
        <Modal id={ACCOUNT_DELETE_CONFIRM_MODAL_ID}
               size="sm"
               showCloseButton={true}
               customCloseButton={(
                   <img src={exitIcon}
                        alt="exit"
                        className="absolute right-[10px] top-[10px] cursor-pointer"
                   />
               )}
               className="!p-0 !rounded-[8px] !shadow-[0px_4px_8px_0px_rgba(16, 24, 64, 0.08)]"
        >
            <div className="relative w-full flex flex-col items-center font-pretendard">
                <p className="whitespace-pre-wrap w-full mt-[44px] px-[24px] py-[16px] text-center font-normal text-[16px] leading-[200%]">
                    {intl.formatMessage({id: "i0067_2"})}
                </p>

                <div className="flex items-center w-full justify-center space-x-[4px] px-[20px] pt-[16px] pb-[32px]">
                    <button tabIndex={-1}
                            className="console-btn-lg-gray w-[118px]"
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({id: "b0004"})}
                    </button>
                    <button tabIndex={-1}
                            className="console-btn-lg-danger min-w-[118px] px-[18px]"
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({id: "i0074"})}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default AccountSetting;
